import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Spinner,
} from '@avila-roffe/components-web';
import { BookOpen, Check, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAllSkillReleasesFlat } from '../../skills/hooks/use-skills';
import type { SkillReleaseFlat } from '../../skills/types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedReleaseIds: string[];
  isSaving: boolean;
  onConfirm: (releaseIds: string[]) => Promise<void>;
}

interface SkillGroup {
  skillId: string;
  skillName: string;
  skillDisplayName: string;
  releases: SkillReleaseFlat[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function releaseStatusVariant(status: string) {
  if (status === 'PUBLISHED') return 'success' as const;
  if (status === 'DEPRECATED') return 'destructive' as const;
  return 'secondary' as const;
}

function groupBySkill(entries: SkillReleaseFlat[]): SkillGroup[] {
  const order: string[] = [];
  const map = new Map<string, SkillGroup>();
  for (const f of entries) {
    if (!map.has(f.skillId)) {
      map.set(f.skillId, {
        skillId: f.skillId,
        skillName: f.skillName,
        skillDisplayName: f.skillDisplayName,
        releases: [],
      });
      order.push(f.skillId);
    }
    map.get(f.skillId)?.releases.push(f);
  }
  return order.map((id) => map.get(id) as SkillGroup);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SkillReleasePickerDialog({
  open,
  onOpenChange,
  selectedReleaseIds,
  isSaving,
  onConfirm,
}: Props) {
  const { data: allEntries = [], isLoading } = useAllSkillReleasesFlat();
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<string[]>(selectedReleaseIds);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally sync only on open
  useEffect(() => {
    if (open) {
      setPending(selectedReleaseIds);
      setQuery('');
      const skillIdsWithSelection = new Set<string>();
      for (const f of allEntries) {
        if (selectedReleaseIds.includes(f.releaseId)) {
          skillIdsWithSelection.add(f.skillId);
        }
      }
      setExpanded(skillIdsWithSelection);
    }
  }, [open]);

  const allGroups = useMemo(() => groupBySkill(allEntries), [allEntries]);

  const filteredGroups = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return allGroups;
    return allGroups
      .map(({ skillId, skillName, skillDisplayName, releases }) => {
        const skillMatches =
          skillName.toLowerCase().includes(q) || skillDisplayName.toLowerCase().includes(q);
        const filteredReleases = skillMatches
          ? releases
          : releases.filter(
              (r) =>
                r.releaseName.toLowerCase().includes(q) ||
                (r.notes ?? '').toLowerCase().includes(q),
            );
        return filteredReleases.length > 0
          ? { skillId, skillName, skillDisplayName, releases: filteredReleases }
          : null;
      })
      .filter(Boolean) as SkillGroup[];
  }, [allGroups, query]);

  // Expand all matching skills when searching
  useEffect(() => {
    if (query.trim()) {
      setExpanded(new Set(filteredGroups.map((g) => g.skillId)));
    }
  }, [query, filteredGroups]);

  function toggleExpand(skillId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) next.delete(skillId);
      else next.add(skillId);
      return next;
    });
  }

  function toggleRelease(skillId: string, releaseId: string) {
    const skillReleaseIds = allEntries.filter((f) => f.skillId === skillId).map((f) => f.releaseId);
    setPending((prev) => {
      const withoutThisSkill = prev.filter((id) => !skillReleaseIds.includes(id));
      const alreadySelected = prev.includes(releaseId);
      return alreadySelected ? withoutThisSkill : [...withoutThisSkill, releaseId];
    });
  }

  function getSelectedReleaseForSkill(skillId: string): string | null {
    const skillReleaseIds = allEntries.filter((f) => f.skillId === skillId).map((f) => f.releaseId);
    return pending.find((id) => skillReleaseIds.includes(id)) ?? null;
  }

  async function handleConfirm() {
    await onConfirm(pending);
    onOpenChange(false);
  }

  const boundCount = pending.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-0 p-0 sm:max-w-lg max-h-[80vh]">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Select Skill Releases</DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search skills or releases…"
              className="pl-9"
              autoFocus
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {boundCount} skill release{boundCount !== 1 ? 's' : ''} selected · one per skill
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-0">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Spinner className="size-5" />
            </div>
          )}

          {!isLoading && filteredGroups.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">
              {query ? 'No skills or releases match your search.' : 'No skill releases available.'}
            </p>
          )}

          {!isLoading &&
            filteredGroups.map(({ skillId, skillName, skillDisplayName, releases }) => {
              const isOpen = expanded.has(skillId);
              const selectedId = getSelectedReleaseForSkill(skillId);
              const selectedRelease = releases.find((r) => r.releaseId === selectedId);

              return (
                <div key={skillId} className="rounded-lg border border-border overflow-hidden">
                  {/* Skill header row */}
                  <button
                    type="button"
                    onClick={() => toggleExpand(skillId)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left bg-muted/30 hover:bg-muted/60 transition-colors"
                  >
                    <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted">
                      <BookOpen className="size-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {skillDisplayName || skillName}
                      </p>
                      <p className="text-[11px] font-mono text-muted-foreground truncate">
                        {skillName}
                      </p>
                    </div>
                    {selectedRelease && (
                      <Badge variant="success" appearance="soft" className="text-[10px] shrink-0">
                        {selectedRelease.releaseName}
                      </Badge>
                    )}
                    {isOpen ? (
                      <ChevronDown className="size-4 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                    )}
                  </button>

                  {/* Release rows */}
                  {isOpen && (
                    <div className="divide-y divide-border">
                      {releases.map((release) => {
                        const isSelected = pending.includes(release.releaseId);
                        return (
                          <button
                            key={release.releaseId}
                            type="button"
                            onClick={() => toggleRelease(skillId, release.releaseId)}
                            data-selected={isSelected}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-muted/40 data-[selected=true]:bg-primary/5"
                          >
                            <div
                              className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                isSelected
                                  ? 'border-primary bg-primary'
                                  : 'border-muted-foreground/40'
                              }`}
                            >
                              {isSelected && <Check className="size-2.5 text-primary-foreground" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-mono font-medium">{release.releaseName}</p>
                              {release.notes && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {release.notes}
                                </p>
                              )}
                            </div>
                            <Badge
                              variant={releaseStatusVariant(release.releaseStatus)}
                              appearance="soft"
                              className="text-[10px] shrink-0"
                            >
                              {release.releaseStatus.charAt(0) +
                                release.releaseStatus.slice(1).toLowerCase()}
                            </Badge>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button
            type="button"
            appearance="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleConfirm()} disabled={isSaving}>
            {isSaving && <Spinner className="size-3.5 mr-2" />}
            Confirm ({boundCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
